/**
 * 외출 늦은 복귀 벌점 설정 (2026-08-11 신설, 2026-09-20 이름 정정)
 *
 * 화장실·휴식 등으로 나간 뒤 기준 시간을 넘겨 돌아오면 건별로 부과한다.
 * 설정은 operational_rules.rules->'outing_overdue' 에 저장.
 *
 * ⚠️ 여기서 정한 '기준 시간'은 출석부·좌석 화면의 '미복귀' 표시에도 같이 쓰인다.
 *    (화면과 벌점 기준이 어긋나면 "화면엔 멀쩡한데 벌점이 붙었다"가 된다)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../config/api';

interface OutingOverdueRule {
  enabled: boolean;
  minutes: number;
  points: number;
  daily_cap: number;
  outing_types: string[];
  notify: boolean;
}

const DEFAULTS: OutingOverdueRule = {
  enabled: false,
  minutes: 15,
  points: 1,
  daily_cap: 5,
  outing_types: ['toilet', 'rest', 'other', 'meal', 'hospital', 'academy'],
  notify: false,
};

const OUTING_TYPES: { code: string; label: string }[] = [
  { code: 'toilet', label: '화장실' },
  { code: 'rest', label: '휴식' },
  { code: 'other', label: '기타' },
  { code: 'meal', label: '식사' },
  { code: 'hospital', label: '병원' },
  { code: 'academy', label: '학원' },
];

const getToken = () =>
  localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken') || '';

const OutingOverdueSettings: React.FC = () => {
  const [form, setForm] = useState<OutingOverdueRule>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/rules`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.outing_overdue) setForm({ ...DEFAULTS, ...data.outing_overdue });
      }
    } catch (e) {
      console.error('외출 미복귀 설정 조회 실패', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleType = (code: string) => {
    setForm((prev) => ({
      ...prev,
      outing_types: prev.outing_types.includes(code)
        ? prev.outing_types.filter((t) => t !== code)
        : [...prev.outing_types, code],
    }));
  };

  const handleSave = async () => {
    if (form.enabled && form.outing_types.length === 0) {
      alert('대상 외출 유형을 하나 이상 선택해 주세요.');
      return;
    }
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE_URL}/api/v1/rules/outing-overdue`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({ detail: '저장 실패' }));
        throw new Error(e.detail || `HTTP ${res.status}`);
      }
      alert('외출 늦은 복귀 설정이 저장되었습니다.');
    } catch (e) {
      alert(`저장 실패: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="pt-4 border-t">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium">외출 늦은 복귀 벌점</div>
          <div className="text-sm text-gray-500">
            외출하고 정해진 시간 안에 돌아오지 않으면 <strong>건별로</strong> 부과됩니다.
            <br />
            시간은 <strong>쉬는시간을 포함해</strong> 나간 시각부터 잽니다. 정규 시간대만
            적용되며(자율학습 구간 제외), 알림은 보내지 않습니다.
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            style={{ width: '1.15rem', height: '1.15rem' }}
            className="rounded accent-blue-600"
          />
          <span className="text-sm text-gray-700">{form.enabled ? '사용 중' : '사용 안 함'}</span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-700">기준 시간</span>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={240}
              value={form.minutes}
              onChange={(e) =>
                setForm({ ...form, minutes: Math.max(1, parseInt(e.target.value) || 1) })
              }
              className="w-20 border rounded-lg px-3 py-2 text-center"
            />
            <span className="text-gray-500 text-sm">분 초과부터</span>
          </div>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-700">건당 점수</span>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={100}
              value={form.points}
              onChange={(e) =>
                setForm({ ...form, points: Math.max(0, parseInt(e.target.value) || 0) })
              }
              className="w-20 border rounded-lg px-3 py-2 text-center"
              disabled={!form.enabled}
            />
            <span className="text-gray-500 text-sm">점</span>
          </div>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-700">하루 상한</span>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={50}
              value={form.daily_cap}
              onChange={(e) =>
                setForm({ ...form, daily_cap: Math.max(0, parseInt(e.target.value) || 0) })
              }
              className="w-20 border rounded-lg px-3 py-2 text-center"
              disabled={!form.enabled}
            />
            <span className="text-gray-500 text-sm">점 (0 = 제한 없음)</span>
          </div>
        </label>
      </div>

      <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
        <b>식사 외출은 위 기준 시간을 쓰지 않습니다.</b> 매장 시간표의 점심·저녁 시간을 기준으로,
        <br />
        ① 식사 시간이 끝났는데 안 돌아온 경우 ② 식사 시간 전에 미리 나간 경우만 검수에 올라갑니다.
        <br />
        <span className="text-amber-700">
          식사 시간 안에 나갔다가 그 안에 돌아온 학생은 잡히지 않습니다. 또한 학생 시간표에 없는
          시간대의 외출은 모든 유형에서 제외됩니다.
        </span>
      </div>

      <div className="mt-3">
        <span className="text-sm text-gray-700">
          대상 외출 유형 <span className="text-gray-400">(식사 외 유형은 같은 기준 시간)</span>
        </span>
        <div className="flex flex-wrap gap-2 mt-1">
          {OUTING_TYPES.map((t) => {
            const on = form.outing_types.includes(t.code);
            return (
              <button
                key={t.code}
                type="button"
                onClick={() => toggleType(t.code)}
                className={`px-3 py-1.5 rounded-full border text-sm ${
                  on
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          ※ 식사·병원처럼 원래 오래 걸리는 외출도 <strong>같은 기준</strong>으로 잽니다. 사정이
          있는 건은 목록을 보고 <strong>원장님이 직접 빼면</strong> 됩니다.
          <br />
          ※ 기준 시간은 <strong>출석부·좌석 화면의 &lsquo;미복귀&rsquo; 표시</strong>에도 함께
          적용됩니다.
          <br />
          ※ 끝내 돌아오지 않고 그대로 하원한 경우는 <strong>조퇴</strong>로 처리되며 여기서는
          중복 부과하지 않습니다.
          <br />
          ※ 벌점은 바로 부과되지 않고 <strong>벌점 검수</strong> 목록에 올라갑니다. 확인 후
          부여하세요.
        </p>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? '저장 중…' : '외출 늦은 복귀 설정 저장'}
      </button>
    </div>
  );
};

export default OutingOverdueSettings;
